import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import "../../styles/AdminStyling/AdminEdit.css";

const EditIcon = `${process.env.PUBLIC_URL}/assets/logos/edit-icon.svg`;

const ToggleSwitch = ({ checked, onChange }) => {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle-slider">{checked ? "Yes" : "No"}</span>
    </label>
  );
};

const AdminEditUser = ({ userId, onClose, onSave }) => {
  const [originalData, setOriginalData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [editableFields, setEditableFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inputRefs = useRef({});

  // Roles are the same as before.
  const roles = [
    "Admin",
    "Leadership",
    "Sponsor",
    "Fellow",
    "Graduate Assistant",
    "Co-op/Intern",
    "Board Member",
  ];
  // Companies (Sponsors) will now be pulled from API.
  const [companies, setCompanies] = useState([]);

  // Fetch user data.
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/get_user`,
          { userid: userId }
        );
        if (response.data.success) {
          setOriginalData(response.data.user);
          setFormData(response.data.user);
        } else {
          setError("User not found.");
        }
      } catch (err) {
        setError("Failed to fetch user data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  // Fetch companies (sponsors) from API.
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/sponsors`
        );
        if (res.data.success) {
          const sponsorList = res.data.sponsors || [];
          // Extract the sponsor names.
          const sponsorNames = sponsorList.map((s) => s.spname);
          setCompanies(sponsorNames);
        } else {
          setCompanies([]);
        }
      } catch (err) {
        console.error("Error fetching companies:", err);
      }
    };

    fetchCompanies();
  }, []);

  const handleEditClick = (field) => {
    setEditableFields((prev) => ({ ...prev, [field]: true }));
    setTimeout(() => {
      if (inputRefs.current[field]) {
        const input = inputRefs.current[field];
        input.focus();
        input.setSelectionRange(input.value.length, input.value.length);
      }
    }, 0);
  };

  const handleCancelClick = (field) => {
    setFormData((prev) => ({ ...prev, [field]: originalData[field] }));
    setEditableFields((prev) => ({ ...prev, [field]: false }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // For multiselect fields using react-select.
  const handleMultiSelectChange = (name, selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
    }));
  };

  const handleToggle = () => {
    setFormData((prev) => ({
      ...prev,
      isactivated: !prev.isactivated,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/edit_user`,
        formData
      );
      if (response.data.success) {
        onSave && onSave();
        onClose && onClose();
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError("Error updating user. Please try again.");
    }
  };

  if (loading)
    return <p className="loading-message">Loading user details...</p>;
  if (error) return <p className="error-message">{error}</p>;

  // Prepare options for react-select. For roles, each option has value and label.
// Sort the roles alphabetically
  const sortedRoles = roles.slice().sort((a, b) => a.localeCompare(b));
  // const roleOptions = roles.map((role) => ({ value: role, label: role }));
  const roleOptions = sortedRoles.map((role) => ({ value: role, label: role }));
  // Sort the companies alphabetically
  const sortedCompanies = companies.slice().sort((a, b) => a.localeCompare(b));
  const companyOptions = sortedCompanies.map((comp) => ({
    value: comp,
    label: comp,
  }));

  return (
    <div className="admin-edit-container">
      <h1 className="admin-edit-heading">Edit User</h1>
      <form className="admin-edit-form" onSubmit={handleSubmit}>
        {[
          { label: "First Name", name: "fname" },
          { label: "Last Name", name: "lname" },
          { label: "Email", name: "email", type: "email", readonly: true },
          { label: "Role", name: "role", type: "multiselect" },
          { label: "Sponsor Access", name: "company", type: "multiselect" },
          { label: "Date Added", name: "datejoined", readonly: true },
          { label: "Date Deactivated", name: "dateleft", readonly: true },
          { label: "Last Login", name: "lastlogin", readonly: true },
        ].map(({ label, name, type = "text", readonly = false }) => (
          <div className="form-group" key={name}>
            <label className="form-label">{label}</label>
            <div className="input-container">
              {readonly ? (
                <span className="readonly-text">
                  {originalData[name] || "--"}
                </span>
              ) : editableFields[name] ? (
                <>
                  {type === "multiselect" ? (
                    <Select
                      isMulti
                      options={name === "role" ? roleOptions : companyOptions}
                      value={
                        formData[name]
                          ? name === "role"
                            ? formData[name].map((r) => ({
                                value: r,
                                label: r,
                              }))
                            : formData[name].map((c) => ({
                                value: c,
                                label: c,
                              }))
                          : []
                      }
                      onChange={(selectedOptions) =>
                        handleMultiSelectChange(name, selectedOptions)
                      }
                      placeholder={`Select ${
                        name === "role" ? "roles" : "companies"
                      }...`}
                      className="input-field multi-select"
                    />
                  ) : (
                    <input
                      type={type}
                      name={name}
                      value={formData[name] || ""}
                      onChange={handleChange}
                      ref={(el) => (inputRefs.current[name] = el)}
                      className="input-field"
                    />
                  )}
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => handleCancelClick(name)}
                    style={{ width: "15%" }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="readonly-text">
                    {Array.isArray(originalData[name])
                      ? originalData[name].join(", ") || "--"
                      : originalData[name] || "--"}
                  </span>

                  {/* Replace the edit button with an edit icon image */}
                  <img
                    alt="Edit"
                    className="user-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(name);
                    }}
                    src={EditIcon}
                  />
                </>
              )}
            </div>
          </div>
        ))}
        <div className="form-group">
          <label className="form-label">Account Activated</label>
          <ToggleSwitch
            checked={formData.isactivated}
            onChange={handleToggle}
          />
        </div>
        <div className="item-actions">
          <button type="submit" className="save-btn">
            Update User
          </button>
          <button type="button" className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminEditUser;
